import type { UserHydraItem } from "@verbleif/lib/src/composables/Api/Type/User/user.interface";
import type { ComputedRef } from "vue";
import type { TaskListScopeDepartment, TaskListScopeMember } from "./taskListScopeTree";
import { storedIcon } from "@verbleif/lib/src/components/Icon/storedIconNames";
import { PermissionScope } from "@verbleif/lib/src/composables/Api/Type/UserPermissions/userPermission.enum";
import { hasMembershipFlag, MembershipFlag } from "@verbleif/lib/src/composables/Api/Type/UserStatus/user_status.enum";
import { computed } from "vue";
import { useAuthStore } from "../AuthStore/useAuthStore";
import { useLocationStore } from "../LocationStore/useLocationStore";
import { taskVoter } from "../Permissions/SharedPermissions";
import { useSharedStore } from "../SharedStore/useSharedStore";

export interface TaskListScopeTreeData {
  departments: ComputedRef<TaskListScopeDepartment[]>
  groupless: ComputedRef<TaskListScopeMember[]>
}

function toMember(user: UserHydraItem): TaskListScopeMember {
  return { id: user.id, name: user.name };
}

/**
 * What the reader may choose in the department tree of Tasks at the selected location, with the
 * read rules of the department lists: a department when its tasks may be read, its members when
 * the tasks of others in it may be read, and the users without a department when theirs may be
 * read. Members are the active task executors, or every active member with `taskSidebarShowAllUsers`.
 */
export function useTaskListScopeTreeData(): TaskListScopeTreeData {
  const { getUsersPerUserGroup, userGroupsList, getUsersFiltered } = useSharedStore();
  const { userPreferences } = useAuthStore();
  const { selectedLocation } = useLocationStore();
  const showAll = computed<boolean>(() => userPreferences.value?.taskSidebarShowAllUsers === true);

  const departments = computed<TaskListScopeDepartment[]>(() => {
    const usersPerUserGroup = getUsersPerUserGroup(showAll.value ? undefined : MembershipFlag.TASK_EXECUTOR);
    return userGroupsList.value
      .filter(userGroup => taskVoter.canReadTasks("userGroup", userGroup))
      .map((userGroup) => {
        const members = taskVoter.canReadTasks("userGroupUser", userGroup)
          ? (usersPerUserGroup[userGroup["@id"]] ?? []).map(toMember)
          : [];
        return {
          id: userGroup.id,
          name: userGroup.name,
          icon: storedIcon(userGroup.avatarIconIdentifier),
          members,
        };
      });
  });

  const groupless = computed<TaskListScopeMember[]>(() => {
    if (!taskVoter.canReadTasks("userGroupLessUser")) {
      return [];
    }
    return getUsersFiltered(selectedLocation.value ?? undefined, false)
      .filter(user => user.statuses.some((status) => {
        if (showAll.value) {
          return status.scope === PermissionScope.LOCATION || status.scope === PermissionScope.CLIENT;
        }
        return status.scope === PermissionScope.LOCATION
          && hasMembershipFlag(status.membershipFlags, MembershipFlag.TASK_EXECUTOR);
      }))
      .map(toMember);
  });

  return { departments, groupless };
}
